/**
 * Was der Server lesen kann — und warum (27.10.2026).
 *
 * Kraftwerk verschlüsselt Ende-zu-Ende (E21), aber nicht alles: Was der Server
 * für Mitteilungen, Buchungen und Kinder-Regeln braucht, bleibt lesbar. Damit
 * diese Einteilung nicht als Fließtext veraltet, steht sie hier als Register,
 * und die Einstellungsseite „Was der Server sieht“ rendert daraus.
 *
 * - Jede Tabelle aus `SYNC_TABLES` steht in `SEALED_EMPTY` (verschlüsselt),
 *   hier in `SERVER_READABLE` (lesbar, mit Grund) — oder in beiden, wenn ein
 *   Teil lesbar bleibt (Gewicht und Schritte am Tagebuchtag, Titel und Ort
 *   von Terminen). Fehlt eine, scheitert schon der Typ und dann der Test
 *   „Was der Server sieht“.
 * - Tabellen, die nur der Server kennt (Anmeldungen, Push-Abos, Schlüssel),
 *   stehen in `SERVER_ONLY_TABLES` — geprüft gegen die echte Datenbank.
 *
 * Neue Tabelle → hier eintragen, mit dem ehrlichen Grund. `notYet` ist kein
 * Grund, sondern das Eingeständnis: der Server braucht es nicht, verschlüsselt
 * ist es trotzdem noch nicht.
 */
import type { EncryptedTable } from './e2e.js';
import type { SyncTable } from './sync.js';

/** Gründe, aus denen der Server etwas lesen muss — in der Reihenfolge der Anzeige. */
export const SERVER_READ_REASONS = [
  // Mitteilungen entstehen, während die App zu ist: Uhrzeit und Text muss er kennen.
  'reminders',
  // Der Wochenrückblick wird sonntags vom Scheduler gerechnet.
  'review',
  // Kurzbefehle schreiben ohne App — Gewicht, Schritte, „genommen“.
  'shortcuts',
  // Taschengeld, Aufgaben-Geld und Sterne bucht der Server selbst.
  'bookings',
  // Was ein Kind sehen und ändern darf, entscheidet der Server.
  'childRules',
  // Wer zu welchem Haushalt gehört, grenzt alles Geteilte ab.
  'household',
  // Das Kalender-Abo (`/ics/…`) holen Apple und Google ohne die App ab.
  'calendarExport',
  // Eingelesene Kalender holt der Server beim Anbieter.
  'calendarImport',
  // Ein Gericht weitergeben kopiert es ins andere Konto.
  'sharing',
  // Neue Vorlagen (Übungen, Pläne) legt der Server ins Konto — er erkennt an
  // der Vorlagen-Kennung, was schon da ist.
  'templates',
  // Der Server braucht es nicht — es ist nur noch nicht verschlüsselt.
  'notYet',
] as const;

export type ServerReadReason = (typeof SERVER_READ_REASONS)[number];

type Reasons = readonly [ServerReadReason, ...ServerReadReason[]];

/**
 * Lesbare Tabellen mit ihren Gründen, der erste ist der hauptsächliche.
 *
 * Jede nicht verschlüsselte Tabelle muss hier stehen (der Typ verlangt es);
 * eine verschlüsselte nur, wenn neben dem Chiffrat Inhalt lesbar bleibt.
 * Belegt im Code: Scheduler (`apps/scheduler/src`), Kurzbefehle
 * (`apps/api/src/quick.ts`), Buchungen (`apps/api/src/pocket.ts`),
 * Kalender (`apps/api/src/calendar`), Weitergeben (`recipe-share.ts`),
 * Vorlagen (`installStarterCatalog`, `POST /account/starter`).
 */
export const SERVER_READABLE = {
  // Zeitzone, Sprache, Uhrzeiten, Module (auch für den Scheduler),
  // Kalorien- und Eiweißziel im Rückblick, Gewicht (Kurzbefehl), Module der
  // Kinder, Wochenzahl im Haushalt. Verschlüsselt (27.10.2026): Größe,
  // Geburtsjahr, Formel, Alltag, Ziel, Kohlenhydrat- und Fettziel.
  profiles: ['reminders', 'review', 'shortcuts', 'childRules', 'household'],
  supplements: ['reminders', 'shortcuts'],
  supplement_intakes: ['reminders', 'shortcuts', 'review'],
  // Legt der Scheduler selbst an.
  reminders: ['reminders'],
  // Verschlüsselt ist alles außer der Vorlagen-Kennung (`catalogKey`).
  exercises: ['templates'],
  // Plan-Tag im „Losgehen um …“.
  training_plans: ['reminders'],
  plan_days: ['reminders'],
  workout_sessions: ['reminders', 'review', 'household'],
  workout_sets: ['review'],
  // Vorbereitungs-Erinnerung (Auftauen, Einweichen) und Gerichte weitergeben.
  foods: ['reminders', 'sharing'],
  recipes: ['reminders', 'sharing'],
  recipe_ingredients: ['reminders', 'sharing'],
  meal_plan_entries: ['reminders'],
  // Tagesprotokoll (28.10.2026, PLANUNG.md (6j)): verschlüsselt sind Name,
  // Herkunft, Menge, Uhrzeit, Kalorien, Kohlenhydrate, Fett, Nährstoffe,
  // Notiz und „geschätzt“. Lesbar bleiben Datum und Platz (die
  // Mahlzeiten-Erinnerung schweigt, wenn schon etwas eingetragen ist) und
  // das Eiweiß (Wochenrückblick: Ø Protein und Ring).
  food_log: ['reminders', 'review'],
  meal_skips: ['reminders'],
  water_log: ['reminders'],
  prep_checks: ['reminders'],
  training_slots: ['reminders', 'review', 'household', 'calendarExport'],
  training_days: ['reminders', 'review', 'household', 'calendarExport'],
  // Verschlüsselt ist nur die Aktivität; Titel, Zeit, Ort, Notiz gehen ins Abo.
  events: ['calendarExport'],
  household_events: ['calendarExport'],
  feed_events: ['calendarImport'],
  // Verschlüsselt ist alles außer Gewicht und Schritten.
  journal_entries: ['shortcuts', 'review'],
  households: ['household'],
  household_members: ['household', 'childRules'],
  pantry_items: ['reminders'],
  tasks: ['reminders', 'bookings', 'childRules'],
  task_completions: ['bookings'],
  pocket_goals: ['bookings', 'childRules'],
  pocket_money: ['bookings', 'childRules'],
  star_entries: ['bookings', 'childRules'],
  // Standort (27.10.2026): verschlüsselt sind Punkt, Name, Radius, Genauigkeit
  // und Art. Lesbar bleiben Haushalt und Verfasser (wer es sehen darf) und
  // die Zeit (Mitteilung nur, solange sie stimmt; nach sieben Tagen löschen).
  places: ['household'],
  location_checkins: ['reminders', 'household', 'childRules'],
  // Akte (27.10.2026): verschlüsselt sind Art, alle Felder und der Verweis
  // in die Planung. Lesbar bleiben Verfasser, Haushalt, Person und
  // Sichtbarkeit — daran entscheidet der Server, wer sie holt: die eigene nur
  // ich, die eines Kindes die Erwachsenen, das Kind nur mit Freigabe.
  record_entries: ['childRules', 'household'],
  // Haushalts-Essensplan (27.10.2026): verschlüsselt sind Gericht (Name,
  // Zutaten, Nährwerte), Notiz und wer kocht. Lesbar bleiben Haushalt (wer
  // es holt), Datum und Platz (die Mahlzeiten-Erinnerung erkennt daran die
  // Protokollzeile „Haushaltsplan“), Wunsch oder Plan und wer ihn angelegt
  // hat (ein Kind ändert nur eigene Wünsche).
  household_meals: ['reminders', 'household', 'childRules'],
  // Wer isst mit: verschlüsselt sind Portion, Zusätzliches, Variante,
  // Hinweis. Lesbar bleiben Eintrag und Person — der Server prüft daran,
  // dass Erwachsene nur für Kinder und jeder für sich schreibt.
  household_meal_eaters: ['household', 'childRules'],
  // Persönliche Abweichungen: verschlüsselt ist alles (nicht mit, Portion,
  // Extras, Variante, Dauerregeln). Lesbar bleibt der Verweis auf den
  // Eintrag — der Server prüft, dass er im eigenen Haushalt liegt.
  meal_deviations: ['household'],
} as const satisfies { readonly [T in Exclude<SyncTable, EncryptedTable>]: Reasons } & {
  readonly [T in EncryptedTable]?: Reasons;
};

export type ServerReadableTable = keyof typeof SERVER_READABLE;

export const SERVER_READABLE_TABLES = Object.keys(SERVER_READABLE) as ServerReadableTable[];

export function serverReadReasons(table: string): readonly ServerReadReason[] {
  return Object.hasOwn(SERVER_READABLE, table) ? SERVER_READABLE[table as ServerReadableTable] : [];
}

/** Lesbare Tabellen nach ihrem hauptsächlichen Grund, in der Reihenfolge der Gründe. */
export function readableByReason(): { reason: ServerReadReason; tables: ServerReadableTable[] }[] {
  return SERVER_READ_REASONS.map((reason) => ({
    reason,
    tables: SERVER_READABLE_TABLES.filter((table) => SERVER_READABLE[table][0] === reason),
  })).filter((group) => group.tables.length > 0);
}

/**
 * Was nur auf ausdrückliche Anforderung kurz lesbar beim Server ankommt — an
 * der Verschlüsselung vorbei, nur im Speicher, nie abgelegt: das Foto zum
 * Kalorienschätzen und die Zusammenfassung für den KI-Coach (27.10.2026).
 * Die App fragt vor jedem Senden und nennt den Empfänger.
 */
export const SERVER_ON_REQUEST = ['photoEstimate', 'coach'] as const;
export type ServerOnRequest = (typeof SERVER_ON_REQUEST)[number];

/** Was der Server über Konto und Geräte selbst führt — nie über den Abgleich. */
export const SERVER_ONLY_KINDS = [
  'account',
  'sessions',
  'passkeys',
  'keys',
  'tokens',
  'push',
  'restTimer',
  'calendarFeeds',
  'codes',
  'estimateJobs',
] as const;

export type ServerOnlyKind = (typeof SERVER_ONLY_KINDS)[number];

/**
 * Tabellen außerhalb des Abgleichs. Der Test „Was der Server sieht“ in der API
 * liest die echten Tabellen aus Postgres: Jede steht in `SYNC_TABLES` oder hier.
 */
export const SERVER_ONLY_TABLES = {
  users: 'account',
  accounts: 'account',
  sessions: 'sessions',
  passkeys: 'passkeys',
  encryption_keys: 'keys',
  household_keys: 'keys',
  api_tokens: 'tokens',
  calendar_tokens: 'tokens',
  push_subscriptions: 'push',
  rest_timers: 'restTimer',
  calendar_feeds: 'calendarFeeds',
  child_codes: 'codes',
  household_invites: 'codes',
  estimate_jobs: 'estimateJobs',
} as const satisfies Record<string, ServerOnlyKind>;
